/**
 * lib/feeArrangements.ts
 * ------------------------------------------------------------------
 * SPECIAL FEE ARRANGEMENTS — payment plans for families in arrears.
 *
 * A family who cannot clear their balance agrees to settle it over a
 * number of months. This module models that agreement and measures
 * whether it is being kept.
 *
 * WHAT THIS IS NOT: a billing channel. No invoice is raised here and no
 * payment is captured here. Invoices and receipts continue through the
 * normal fee screens exactly as before — an arrangement only records what
 * was AGREED, and compares it against what actually arrived. Making it a
 * second place to take money would put the family's balance in two
 * systems that could disagree.
 *
 * ADHERENCE IS COMPUTED, NEVER STORED. "Paid to date" is a reduce over the
 * family's real payments since the plan started; "expected to date" is a
 * reduce over the schedule up to today. A stored `paidSoFar` would drift
 * the moment a receipt was captured, voided or backdated — and drift here
 * means telling a family they have defaulted when they have not.
 *
 * The schedule IS stored, because it is the agreement: recomputing it
 * later from a changed balance would silently rewrite what the family
 * signed up to.
 * ------------------------------------------------------------------ */

export const ARRANGEMENT_COLLECTION = 'financeArrangements';

export type ArrangementStatus = 'active' | 'completed' | 'defaulted' | 'cancelled';
export type ArrangementFrequency = 'monthly' | 'fortnightly' | 'termly';

export interface ArrangementInstalment {
  /** ISO date the instalment falls due. */
  dueDate: string;
  amount: number;
}

export interface FeeArrangement {
  id: string;
  familyId: string;
  familyName: string;
  /** The arrears the plan was agreed against — frozen at agreement time, so
   *  later billing does not silently change what was agreed. */
  openingBalance: number;
  startDate: string;
  frequency: ArrangementFrequency;
  instalmentCount: number;
  instalmentAmount: number;
  schedule: ArrangementInstalment[];
  status: ArrangementStatus;
  /** Free text — "father lost his job, reviewed each term". */
  note?: string;
  agreedWith?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  closedReason?: string;
}

const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** Add n periods to an ISO date, clamping to month end (31 Jan + 1 month = 28/29 Feb). */
function addPeriods(iso: string, n: number, freq: ArrangementFrequency): string {
  const d = new Date(`${iso}T00:00:00`);
  if (freq === 'fortnightly') {
    d.setDate(d.getDate() + n * 14);
    return d.toISOString().slice(0, 10);
  }
  const months = freq === 'termly' ? n * 4 : n;
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d.toISOString().slice(0, 10);
}

/**
 * Split a balance into equal instalments, with the ROUNDING REMAINDER ON THE
 * FIRST payment rather than the last.
 *
 * Putting it first means the plan finishes exactly on the final due date and
 * the family is never left owing 43 thebe after their last payment — which
 * looks like a default in every report and generates a chase letter over
 * nothing.
 */
export function buildSchedule(
  balance: number,
  count: number,
  startDate: string,
  frequency: ArrangementFrequency = 'monthly',
): ArrangementInstalment[] {
  const total = round2(balance);
  if (!(total > 0) || !(count > 0)) return [];
  const even = Math.floor((total / count) * 100) / 100;
  const remainder = round2(total - even * count);
  return Array.from({ length: count }, (_, i) => ({
    dueDate: addPeriods(startDate, i, frequency),
    amount: i === 0 ? round2(even + remainder) : even,
  }));
}

export interface ArrangementProgress {
  /** Real money received against this family since the plan started. */
  paidToDate: number;
  /** What the schedule says should have arrived by `asOf`. */
  expectedToDate: number;
  /** paidToDate - expectedToDate. Negative = behind. */
  variance: number;
  /** Still to pay under the plan. */
  remaining: number;
  /** 0-100, against the opening balance. */
  percentComplete: number;
  /** How many scheduled instalments have fallen due. */
  instalmentsDue: number;
  /** Whole instalments covered by what has actually been paid. */
  instalmentsCovered: number;
  /** Instalments' worth of arrears — the number people actually quote. */
  instalmentsBehind: number;
  state: 'on_track' | 'ahead' | 'behind' | 'settled' | 'not_started';
  /** The next instalment not yet covered by payments received. */
  nextDue?: ArrangementInstalment;
}

/**
 * Measure a plan against reality.
 *
 * `paymentsSinceStart` is the sum of the family's actual receipts dated on or
 * after the plan start — taken from the normal payment records, never from
 * anything this module writes.
 *
 * A one-thebe shortfall is not a default, so `tolerance` (default P1) keeps
 * rounding noise from flagging a family who has paid in full.
 */
export function assessArrangement(
  a: Pick<FeeArrangement, 'schedule' | 'openingBalance' | 'startDate' | 'status'>,
  paymentsSinceStart: number,
  asOf: Date = new Date(),
  tolerance = 1,
): ArrangementProgress {
  const today = asOf.toISOString().slice(0, 10);
  const paidToDate = round2(Math.max(0, paymentsSinceStart));
  const due = a.schedule.filter((s) => s.dueDate <= today);
  const expectedToDate = round2(due.reduce((t, s) => t + s.amount, 0));
  const variance = round2(paidToDate - expectedToDate);
  const remaining = round2(Math.max(0, a.openingBalance - paidToDate));
  const percentComplete = a.openingBalance > 0
    ? Math.min(100, Math.round((paidToDate / a.openingBalance) * 1000) / 10)
    : 100;

  // Which instalments has the money actually covered, in order?
  let running = paidToDate;
  let covered = 0;
  for (const s of a.schedule) {
    if (running + tolerance >= s.amount) { running = round2(running - s.amount); covered += 1; } else break;
  }
  const nextDue = a.schedule[covered];

  let state: ArrangementProgress['state'];
  if (a.status === 'completed' || remaining <= tolerance) state = 'settled';
  else if (today < a.startDate) state = 'not_started';
  else if (variance < -tolerance) state = 'behind';
  else if (variance > tolerance) state = 'ahead';
  else state = 'on_track';

  return {
    paidToDate,
    expectedToDate,
    variance,
    remaining,
    percentComplete,
    instalmentsDue: due.length,
    instalmentsCovered: covered,
    instalmentsBehind: Math.max(0, due.length - covered),
    state,
    nextDue,
  };
}

export const STATE_LABEL: Record<ArrangementProgress['state'], string> = {
  on_track: 'On track',
  ahead: 'Ahead of plan',
  behind: 'Behind plan',
  settled: 'Settled',
  not_started: 'Not started',
};

/** How overdue, in days, the earliest uncovered instalment is. 0 if none. */
export function daysBehind(p: ArrangementProgress, asOf: Date = new Date()): number {
  if (p.state !== 'behind' || !p.nextDue) return 0;
  const due = new Date(`${p.nextDue.dueDate}T00:00:00`).getTime();
  return Math.max(0, Math.floor((asOf.getTime() - due) / 86400000));
}
